import * as React from 'react';
import { useLocation } from 'react-router';

import { throttle } from 'throttle-debounce';

import { useIsAppsSeoPage } from '@base/hooks';
import { isMobileScreen } from '@base/utils';

interface IProps {
	wrapperRef: React.RefObject<HTMLDivElement>;
	stickyHeaderRef: React.RefObject<HTMLDivElement>;
	heightToStick: number;
}

const APP_COVER_COEFFICIENT = 2.638;
const MIN_SCROLL_SHIFT = 10;

export const useSticky = ({
	wrapperRef,
	stickyHeaderRef,
	heightToStick,
}: IProps) => {
	const isAppSeoPage = useIsAppsSeoPage();
	const [isFixed, setIsFixed] = React.useState(false);
	const [isVisible, setIsVisible] = React.useState(true);
	const [isAnimated, setIsAnimated] = React.useState(false);
	const [isTransparent, setIsTransparent] = React.useState(isAppSeoPage);
	let prevScrollOffset = 0;
	const { pathname } = useLocation();
	const update = () => {
		const currentScrollOffset = window.scrollY || window.pageYOffset;
		const isScrollingDown = currentScrollOffset > prevScrollOffset;
		const headerHeight = stickyHeaderRef ? stickyHeaderRef.current?.clientHeight : 0;
		const stickyHeight = heightToStick || headerHeight;
		const coverHeight = (window && isAppSeoPage) ? (window?.innerWidth / APP_COVER_COEFFICIENT) : 0;

		if (prevScrollOffset === currentScrollOffset) {
			return;
		}

		// скролим ВНИЗ
		if (isScrollingDown) {
			if (currentScrollOffset >= stickyHeight) {
				!isAppSeoPage && setIsFixed(true);
				isAppSeoPage && setIsAnimated(true);
				setIsVisible(false);
			} else {
				!isAppSeoPage && setIsFixed(false);
				isAppSeoPage && setIsVisible(true);
				setIsAnimated(false);
			}

			if (isAppSeoPage) {
				if (currentScrollOffset <= coverHeight) {
					setIsTransparent(true);
				}
			}

		// скролим ВВЕРХ
		} else {
			const isBounceScroll = currentScrollOffset >= document.body.clientHeight - window.innerHeight;
			const isSmallScroll = Math.abs(prevScrollOffset - currentScrollOffset) < MIN_SCROLL_SHIFT && currentScrollOffset > stickyHeight * 2;

			if ( isBounceScroll) {
				return;
			}

			if (currentScrollOffset === 0) {
				setIsFixed(false);
			}

			// если скрол выше шапки
			if (currentScrollOffset <= stickyHeight && (!isFixed || !isVisible)) {
				setIsAnimated(false);
				setIsVisible(true);
			// если скрол ниже шапки
			} else if (!isSmallScroll) {
				setIsFixed(true);
				setIsAnimated(true);
				setIsVisible(true);
			}

			if (isAppSeoPage) {
				if (currentScrollOffset > coverHeight ) {
					setIsTransparent(false);
				}

				if (currentScrollOffset < coverHeight - stickyHeight/2 ) {
					setIsTransparent(true);
				}
			}
		}

		prevScrollOffset = currentScrollOffset;
	};
	const reset = () => {
		setIsFixed(true);
		setIsVisible(true);
		setIsAnimated(false);
	};
	const isAppsSeoPage = useIsAppsSeoPage();
	// задаёт высоту хедера для отступа контента под ним
	const adaptHeaderHeight = function() {
		if (!wrapperRef.current) {
			return;
		}

		setTimeout(() => {
			if (!isAppsSeoPage || !isMobileScreen()) {
				wrapperRef.current.style.height = stickyHeaderRef.current.clientHeight + 'px';
			} else {
				wrapperRef.current.style.height = '0px';
			}
		});
	};
	const onScroll = throttle(100, update);
	const onResize = throttle(1000, () => {
		adaptHeaderHeight();

		if (isMobileScreen()) {
			window.addEventListener('scroll', onScroll, {
				capture: true,
				passive: true,
			});
		} else {
			setIsAnimated(false);
			setIsFixed(true);
			setIsVisible(true);
			window.removeEventListener('scroll', onScroll, {
				capture: true,
			});
		}
	});

	React.useEffect(() => {
		setIsTransparent(isAppsSeoPage);
		adaptHeaderHeight();
		update();
		reset();

		if (isMobileScreen()) {
			window.addEventListener('scroll', onScroll, {
				capture: true,
				passive: true,
			});
		}
		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('scroll', onScroll, {
				capture: true,
			});
			window.removeEventListener('resize', onResize);
		};
	}, [pathname]);

	return {
		isFixed,
		isVisible,
		isAnimated,
		isTransparent,
	};
};

